/**
 * useTutorLiveFeed — le fil de la séance du tuteur, la session avec le champion, la génération (la colonne de
 * droite de `<TutorLivePage>`).
 *
 * Ce qu'il lit (tech-derisk, point 4) : les tranches Redux qu'écrit l'écouteur global `LKEvents`
 * (`liveMode.raisedHands`, `outOfAppIds`, `timesSinceLastCall`, `soundModeIds`, `ongoingCheckpointIds`,
 * `liveParticipants`, `recentFinalizedActivities`, `autoModeStartedActivities`, `completedConversations`) et la
 * mémoire de l'ancien panneau (`data.tutorEventsPanel` : la croix, l'instant de chaque signal, l'envoi du
 * champion), jamais le label `events-panel`. Ce qu'il garde (point 8) : la vie de chaque carte dans
 * `data.tutorLiveSession`, rangée par compte et par séance.
 *
 * Ce qu'il rend :
 *  - `zone` : « À traiter », ce qui attend le masteur chez tous les élèves (`decisions`), et le champ qui génère pour
 *    l'élève regardé (tout le groupe en mosaïque) ;
 *  - `threads` : le fil de chaque élève, que l'écran pose sur sa tuile ; `replay` : la séance de l'élève regardé,
 *    l'onglet « Fil » du centre ;
 *  - `layer` : l'accusé d'un geste (snackbar), et la feuille du moment (le formulaire, le recadrage, l'aperçu) ;
 *  - `samSummary` : la feuille des résumés du champion (`checkpoint-rh`) ;
 *  - `generate(studentId?, from?)` : « Ajouter une activité » de la pellicule et « Générer à partir de l'exo » du
 *    centre ; `addActivity()` : « + Activité » de la barre — le champ ressort, pré-rempli pour l'élève visé ;
 *  - `pending` : ce qui attend une décision chez chaque élève, que l'écran pose sur `students.list[].pending`
 *    (la cloche et la vignette).
 *
 * La popup de lancement du champion (`TutorCheckpointManager`, `checkpoint-rh`) n'a pas de port : en prod,
 * toute salle `events-panel` la fait taire (`skipCheckpoint`, `LiveParticipant.js:277`), et l'écran ne
 * s'ouvre que là. « Envoyer » le champion reste la carte du fil.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"

import { syncTutorEventsStartedAt } from "../../../../../../data/redux/reducers/data/tutorEventsPanel"
import {
    appendTutorLiveSession,
    pruneTutorLiveSession,
    seenTutorLiveSession,
    syncTutorLiveSession,
    tutorLiveSessionKey,
} from "../../../../../../data/redux/reducers/data/tutorLiveSession"
import { liveServiceV2 } from "../../../../../../data/services/liveServiceV2"
import { schoolAssessmentsService } from "../../../../../../data/services/schoolAssessmentsService"
import { hasFeature } from "../../../../../../data/utility/Utils"
import { type TutorSessionReplayProps } from "../../../../../../design-system/organisms/TutorSessionReplay/TutorSessionReplay"
import {
    type TutorLiveFeedZone,
    type TutorLiveOverlaysZone,
} from "../../../../../../design-system/pages/TutorLivePage/TutorLivePage"
import { apiCall } from "../../../../../../engine/shared/apiCall"
import { IS_WEB } from "../../../../../../engine/shared/platform"
import { track } from "../../../hooks/trackEvent"
import { type UITutorLiveStudent, type UITutorSessionRibbon } from "../../../types/tutorLive"
import { selectAccount, selectAccountStudents } from "../../../../view-models/shared/displayedStudent"
import {
    activityChapter,
    activityReviewStatus,
    clockOf,
    enrolledStudents,
    generationPrefill,
    helperLines,
    isQuizConversation,
    quizLines,
    type ApiLiveSessionUser,
    type ApiStudentLists,
} from "../../../../adapters/tutor/Live/tutorLiveFeedAdapter"
import {
    resolveSessionObjective,
    sessionSubjectIds,
    type ApiObjectiveStudent,
} from "../../../../adapters/tutor/Live/sessionObjectiveAdapter"
import {
    dialogueWithStudentId,
    isOutOfApp,
    quizParticipantIds,
    signalStartTimes,
    tutorLiveSignals,
} from "../../../../view-models/tutor/Live/tutorLiveFeedRules"
import {
    tutorLiveFeedItems,
    tutorLiveFeedNextWake,
    tutorLiveFeedSync,
    tutorLiveFeedView,
    tutorLiveTimelineChanges,
    tutorSessionThreads,
    isoOf,
    joinedCallTimes,
    quizEntryOf,
    type TutorLiveCompletedConversation,
    type TutorLiveFeedRecord,
    type TutorLiveTransientEntry,
} from "../../../../view-models/tutor/Live/tutorLiveFeedViewModel"
import { rawId, type TutorLiveLayer, type TutorLiveRoomContext, type TutorLiveSelection } from "./tutorLiveContract"
import { type TutorLiveGenerateSource } from "./useTutorLiveCenterActions"
import { type TutorLiveGenerateRequest } from "./useTutorLiveChromeChat"
import { type TutorLiveFeedGenerationRequest } from "./useTutorLiveFeedGeneration"
import { useTutorLiveFeedActions } from "./useTutorLiveFeedActions"
import { useTutorLiveFeedGeneration } from "./useTutorLiveFeedGeneration"
import { useTutorLiveFeedProposals, type TutorLiveHelperProposalMemory } from "./useTutorLiveFeedProposals"
import { useTutorLiveFeedSamSummary } from "./useTutorLiveFeedSamSummary"
import { useTutorLiveFeedSheet } from "./useTutorLiveFeedSheet"

/** La mémoire née plus tard que le début du live : elle a commencé ailleurs, la colonne le dit (point 8). */
const STARTED_ELSEWHERE_MS = 2 * 60 * 1000

/** Les mesures de l'ancienne popup du résumé (`ConversationSummaryPopup`, F-100) : son affichage, sa croix. */
export const SUMMARY_POPUP_EVENTS = {
    display: { name: "checkpoint.summarypopup.display" },
    close: { name: "checkpoint.summarypopup.close" },
} as const

/** Une demande de génération, telle que les trois appelants la font. */
export type TutorLiveGenerateCall = {
    /** Les élèves visés ; vide : tout le groupe. */
    studentIds: string[]
    from?: TutorLiveGenerateSource
    attachment?: TutorLiveGenerateRequest["attachment"]
    /** D'où vient la pièce : une photo du chat (l'heure de son message), sinon le trombone. */
    origin?: TutorLiveFeedGenerationRequest["origin"]
}

/** La source d'une semblable, telle que la génération la prend (« Générer à partir de l'exo », « /similaire »). */
function generationFrom(source: TutorLiveGenerateSource): NonNullable<TutorLiveFeedGenerationRequest["from"]> {
    return {
        materialId: source.materialId,
        title: source.title,
        level: source.level,
        confidence: source.confidence,
        subjectId: source.subjectId,
        className: source.className,
        preview: source.preview,
    }
}

/**
 * Le chapitre de l'activité qu'un élève a devant lui (`onScreen`), lu dans ses listes : la photo du chat le reprend.
 * Aucun : `null`.
 */
function chapterOnScreen(
    student: UITutorLiveStudent | undefined,
    accountStudents: (ApiStudentLists & { id?: string | number })[]
): TutorLiveFeedGenerationRequest["chapter"] | null {
    const activityId = student?.activities.find((activity) => activity.onScreen === true)?.id
    if (student == null || activityId == null) return null
    return activityChapter(
        accountStudents.find((candidate) => String(candidate?.id) === student.id),
        activityId
    )
}

export type TutorLiveFeed = {
    zone: TutorLiveFeedZone
    /**
     * Le début de la séance, celui de la barre de séance : l'heure officielle, ou le premier Dialogue d'un élève s'il est
     * plus tôt (`tutorSessionStart`). Le chrono « LIVE » de la barre du bas compte de là : avant l'heure officielle, il
     * disait 00:00 pendant que la barre de séance comptait déjà.
     */
    sessionStartedAt: number
    layer: TutorLiveLayer
    samSummary: TutorLiveOverlaysZone["samSummary"]
    /**
     * Générer, pour les trois appelants de l'écran : la pellicule (`{ studentIds: [élève] }`), le centre
     * (`{ studentIds: [élève], from }`, « Générer à partir de cet exo »), la barre et le chat
     * (`{ studentIds, attachment }`, vide : tout le groupe).
     */
    generate: (request: TutorLiveGenerateCall) => void
    /**
     * Toucher la tuile d'un élève, même celui qu'on regarde déjà : le champ revient sur lui (une demande pour le groupe
     * l'avait tourné vers tout le groupe).
     */
    watchStudent: () => void
    /** Le fil de chaque élève, depuis le début du live : `students.list[].thread`. */
    threads: Record<string, UITutorSessionRibbon | null>
    /** La séance de l'élève regardé, relue en grand : l'onglet « Fil » du centre. En mosaïque : aucune. */
    replay: TutorSessionReplayProps | null
    /** Ce qui attend une décision chez chaque élève : `students.list[].pending`. */
    pending: Record<string, number>
    /**
     * Les élèves dont la main levée attend un choix pendant que le masteur parle à un autre (leur carte propose
     * d'envoyer le champion) : `students.list[].handChoice`. La même référence tant que la liste ne change pas.
     */
    handChoice: string[]
    /**
     * Les deux réponses de l'ancienne fenêtre de la main levée (`CheckpointLaunchPopup`), depuis sa carte de la
     * pellicule : `send_sam` (« Oui, vas-y » : le geste « Envoyer » de sa carte du bac, au même protocole) ou
     * `decline` (« Non » : la carte du bac s'écarte, la main reste levée ; l'élève ne recevait rien d'un refus).
     */
    onHandChoice: (studentId: string, choice: "send_sam" | "decline") => void
    /**
     * La carte en vol (F-62) : l'écran la pose sur `TutorLiveFeedZone.pending`, à part de la zone (elle change
     * sans que le fil change).
     */
    feedPending: { itemId: string; actionId: string } | null
    /**
     * « Actualiser » (F-12, le `sync-alt` de `NewStudentsListItem`) : relit la séance, l'appel, et les
     * évaluations à venir de l'élève regardé. Le fil n'a pas de place pour le bouton : il vit dans « Plus ».
     */
    refresh: { onRefresh: () => void; refreshing: boolean }
    /** « + Activité » de la barre : le champ ressort pour l'élève visé (ou tout le groupe), pré-rempli. */
    addActivity: () => void
}

/** Le prénom de l'élève avec le champion : celui qu'on regarde d'abord, sinon le premier. Aucun : `null`. */
function firstnameWithHelper(
    ongoing: string[],
    watchedId: string | null,
    firstnameOf: (studentId: string) => string
): string | null {
    const id = watchedId != null && ongoing.includes(watchedId) ? watchedId : ongoing[0]
    return id != null ? firstnameOf(id) : null
}

type Ids = (string | number)[] | null | undefined
const strings = (ids: Ids): string[] => (ids ?? []).map(String)
const byString = <T>(map: Record<string, T> | unknown[] | null | undefined): Record<string, T> =>
    map != null && !Array.isArray(map) ? (map as Record<string, T>) : {}

type CompletedRaw = {
    liveSessionId?: unknown
    studentId?: string | number
    read?: boolean
    timestamp?: number
    conversation?: {
        id?: string | number
        summary?: string
        summary_title?: string
        status?: string
        trigger_context?: string
    }
}

/** Les tranches lues, en une passe (`shallowEqual` : une tranche qui ne bouge pas ne relance rien). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function selectFeedSlices(state: any) {
    const liveMode = state?.liveMode ?? {}
    const panel = state?.data?.tutorEventsPanel ?? {}
    return {
        raisedHands: liveMode.raisedHands as Ids,
        outOfAppIds: liveMode.outOfAppIds as Ids,
        activityCameraOnIds: liveMode.activityCameraOnIds as Ids,
        timesSinceLastCall: liveMode.timesSinceLastCall as Record<string, number> | undefined,
        soundModeIds: liveMode.soundModeIds as Ids,
        ongoingCheckpointIds: liveMode.ongoingCheckpointIds as Ids,
        liveParticipants: liveMode.liveParticipants as Ids,
        todayLogs: liveMode.todayLiveSessionLogs as Parameters<typeof joinedCallTimes>[0] | undefined,
        recentFinalizedActivities: liveMode.recentFinalizedActivities,
        autoModeStartedActivities: liveMode.autoModeStartedActivities,
        completedConversations: liveMode.completedConversations as CompletedRaw[] | undefined,
        dismissed: panel.dismissedEventHashes as string[] | undefined,
        startedAtByKey: panel.startedAtByKey as Record<string, number> | undefined,
        samSentAtByKey: panel.samSentAtByKey as Record<string, number> | undefined,
        helperProposals: panel.helperProposals as Record<string, TutorLiveHelperProposalMemory | undefined> | undefined,
        sessions: state?.data?.tutorLiveSession?.sessions as
            | Record<
                  string,
                  {
                      createdAt?: number
                      entries?: TutorLiveFeedRecord[]
                      timeline?: TutorLiveFeedRecord[]
                      seen?: Record<string, number>
                  }
              >
            | undefined,
        account: selectAccount(state),
        accountStudents: selectAccountStudents(state) as (ApiStudentLists & { id?: string | number })[],
    }
}

export function useTutorLiveFeed(
    room: TutorLiveRoomContext,
    selection: TutorLiveSelection,
    students: UITutorLiveStudent[],
    /**
     * `mosaic` : la mosaïque est montrée ; son fil n'est jamais filtré, et le champ vise tout le groupe.
     * `presenting` : une présentation part ou dure ; la relecture ne s'ouvre alors que d'un geste.
     * `chatOpen` : le chat est ouvert ; une image glissée ou collée lui reste. `centerOnly` : tout l'écran est partagé.
     * `similarSource` : l'activité ouverte au centre pour un élève (« /similaire »).
     */
    options: {
        mosaic?: boolean
        presenting?: boolean
        chatOpen?: boolean
        centerOnly?: boolean
        similarSource?: (studentId: string) => TutorLiveGenerateSource | null
    } = {}
): TutorLiveFeed {
    const dispatch = useDispatch()
    const slices = useSelector(selectFeedSlices, shallowEqual)
    const liveSessionId = room.liveSessionId
    const sessionId = liveSessionId != null ? String(liveSessionId) : ""
    const accountId = slices.account?.id != null ? String(slices.account.id) : room.me.id
    const memoryKey = tutorLiveSessionKey(accountId, liveSessionId) as string | null
    const memory = memoryKey != null ? slices.sessions?.[memoryKey] : undefined
    const records = useMemo(() => memory?.entries ?? [], [memory?.entries])
    // Le fil et la chronologie (le mode du son, l'activité devant chaque élève), gardés à part : le ruban et les
    // activités du fil les lisent ensemble.
    const withTimeline = useMemo(
        () => (memory?.timeline != null && memory.timeline.length > 0 ? [...records, ...memory.timeline] : records),
        [records, memory?.timeline]
    )

    // La séance : ses inscrits (`liveSession.users`), ses labels.
    const liveSession = useMemo(
        () =>
            (
                (slices.account?.live_sessions ?? []) as {
                    id?: unknown
                    users?: ApiLiveSessionUser[]
                    live_room?: unknown
                }[]
            ).find((session) => String(session?.id) === sessionId),
        [slices.account?.live_sessions, sessionId]
    )
    const users = useMemo(() => liveSession?.users ?? [], [liveSession?.users])
    const checkpointRh = hasFeature(liveSession?.live_room, "checkpoint-rh") as boolean
    const quizAvailable = hasFeature(liveSession?.live_room, "prerequisite-quiz") as boolean
    const studentIds = useMemo(() => {
        const enrolled = enrolledStudents(users).map((user) => String(user.id))
        return enrolled.length > 0 ? enrolled : students.map((student) => student.id)
    }, [users, students])
    const names = useMemo(
        () =>
            studentIds.map((id) => {
                const shown = students.find((student) => student.id === id)
                const user = users.find((u) => String(u.id) === id)
                return {
                    id,
                    firstname: shown?.firstname ?? user?.firstname ?? user?.display_name ?? "L'élève",
                    avatarUrl: shown?.avatarUrl,
                }
            }),
        [studentIds, students, users]
    )
    const namesRef = useRef(names)
    namesRef.current = names
    const firstnameOf = useCallback(
        (id: string) => namesRef.current.find((name) => name.id === id)?.firstname ?? "L'élève",
        []
    )

    // ── L'horloge et l'anti-rebond de la sortie de l'appli. L'heure est lue à chaque rendu ; l'ancien `tick`
    // (1 Hz) redessinait toute la salle, vidéos comprises : ici un seul minuteur, posé sur la prochaine
    // échéance du fil (`tutorLiveFeedNextWake`), réveille l'écran — et aucun quand rien n'attend.
    const [, wake] = useReducer((count: number) => count + 1, 0)
    const now = Date.now()
    const outSinceRef = useRef<Record<string, number>>({})
    const outOfApp = strings(slices.outOfAppIds)
    const outKey = outOfApp.join(",")
    // Le premier instant vu hors de l'appli, par élève (l'ancien `outOfAppDebounceRef`), tenu au rendu : les
    // signaux le lisent dans le même rendu.
    useMemo(() => {
        const current = new Set(outKey === "" ? [] : outKey.split(","))
        const next: Record<string, number> = {}
        current.forEach((id) => (next[id] = outSinceRef.current[id] ?? Date.now()))
        outSinceRef.current = next
    }, [outKey])

    const present = strings(slices.liveParticipants)
    // Photographier sa copie n'est pas sortir : la caméra d'activité retire l'élève des sorties, pour les hachures
    // comme pour l'alerte (une seule lecture, la règle de la tuile).
    const cameraOn = strings(slices.activityCameraOnIds)
    const outOfAppSince = Object.fromEntries(
        Object.entries(outSinceRef.current).filter(([id]) => !cameraOn.includes(id))
    )
    const soundModeIds = strings(slices.soundModeIds)
    const raisedHands = strings(slices.raisedHands)
    // Un élève dans son quiz de prérequis n'est pas avec le champion (`quizParticipantIds`) : ni trace, ni aurore.
    const quizIds = quizParticipantIds(room.live?.participants ?? [])
    const ongoing = strings(slices.ongoingCheckpointIds).filter((id) => !quizIds.includes(id))
    const signals = tutorLiveSignals({
        studentIds,
        present,
        raisedHands,
        outOfAppSince,
        timesSinceLastCall: byString(slices.timesSinceLastCall),
        soundModeIds,
        ongoingCheckpointIds: ongoing,
        recentFinalizedActivities: byString(slices.recentFinalizedActivities),
        autoModeStartedActivities: byString(slices.autoModeStartedActivities),
        now,
    })
    const times = signalStartTimes(signals, slices.startedAtByKey, now)

    // Les instants persistés (`syncTutorEventsStartedAt`), écrits au changement seulement.
    const additionsKey = Object.keys(times.additions).join("|")
    const removalsKey = times.removals.join("|")
    const timesRef = useRef(times)
    timesRef.current = times
    useEffect(() => {
        const { additions, removals } = timesRef.current
        if (additionsKey === "" && removalsKey === "") return
        dispatch(
            syncTutorEventsStartedAt({
                additions: additionsKey !== "" ? additions : undefined,
                removals: removalsKey !== "" ? removals : undefined,
            }) as never
        )
    }, [dispatch, additionsKey, removalsKey])

    // Les résumés du champion qui attendent d'être lus (l'ancienne popup, sous `checkpoint-rh` seulement).
    const completed = useMemo<TutorLiveCompletedConversation[]>(
        () =>
            !checkpointRh
                ? []
                : (slices.completedConversations ?? [])
                      .filter(
                          (entry) =>
                              entry?.read !== true &&
                              String(entry?.liveSessionId) === sessionId &&
                              entry?.conversation?.id != null &&
                              entry.studentId != null
                      )
                      .map((entry) => ({
                          studentId: String(entry.studentId),
                          conversationId: String(entry.conversation?.id),
                          at: entry.timestamp ?? Date.now(),
                          title: entry.conversation?.summary_title ?? undefined,
                          summary: entry.conversation?.summary ?? "",
                          needsHelp: entry.conversation?.status === "tutor_needed",
                          quiz: isQuizConversation(entry.conversation),
                      })),
        [checkpointRh, slices.completedConversations, sessionId]
    )

    const dialogueWith = dialogueWithStudentId(soundModeIds, studentIds, present)
    const cardsInput = {
        signals,
        startedAt: times.startedAt,
        samSentAtByKey: slices.samSentAtByKey,
        dismissed: slices.dismissed ?? [],
        completed,
        students: names,
        reviewStatus: (studentId, activityId) =>
            activityReviewStatus(
                slices.accountStudents.find((student) => String(student?.id) === studentId),
                activityId
            ),
        dialogueWith,
        isWeb: IS_WEB,
        helperName: room.helperName,
        now,
    }
    const { cards, traces } = tutorLiveFeedItems(cardsInput)

    // F-100 : la mesure de l'ancienne popup du résumé, une fois par conversation.
    const displayedRef = useRef(new Set<string>())
    useEffect(() => {
        for (const card of cards) {
            if (card.signal !== "sam_summary" || displayedRef.current.has(card.id)) continue
            displayedRef.current.add(card.id)
            track(SUMMARY_POPUP_EVENTS.display)
        }
    })

    // ── La mémoire : la séance de la clé seule, les cartes nées et tombées.
    useEffect(() => {
        if (memoryKey != null) dispatch(pruneTutorLiveSession(memoryKey) as never)
    }, [dispatch, memoryKey])
    const sync = tutorLiveFeedSync(
        records,
        cards,
        { students: names, helperName: room.helperName, now, samSentAtByKey: slices.samSentAtByKey },
        traces
    )
    const syncKey = `${sync.opened.map((r) => r.id).join(",")}|${sync.closed.map((c) => c.id).join(",")}`
    const syncRef = useRef(sync)
    syncRef.current = sync
    useEffect(() => {
        if (memoryKey == null || syncKey === "|") return
        dispatch(syncTutorLiveSession(memoryKey, syncRef.current) as never)
    }, [dispatch, memoryKey, syncKey])

    // Le mode du son et l'activité devant chaque élève, gardés à chaque changement : le ruban de la séance et les
    // traces rangées sous leur activité s'en déduisent (V1 : ce que ce navigateur a vu depuis l'ouverture de la salle).
    const timeline = tutorLiveTimelineChanges(
        withTimeline,
        students.map((student) => ({
            studentId: student.id,
            // Absent, son mode par défaut ne dit rien de la séance : le début de la barre ne le lit pas.
            mode: student.presence === "here" ? student.soundMode : "away",
            focusId: student.activities.find((activity) => activity.onScreen === true)?.id ?? null,
            // Sorti de l'appli depuis 3 s : ses hachures sur le fil, bien avant l'alerte (3 min).
            ...(student.presence === "here" && {
                outOfApp: isOutOfApp(outOfAppSince[student.id], now, false),
            }),
        })),
        now
    )
    const timelineKey = timeline.map((change) => `${change.kind}:${change.studentId}:${change.value}`).join("|")
    const timelineRef = useRef(timeline)
    timelineRef.current = timeline
    useEffect(() => {
        if (memoryKey == null || timelineKey === "") return
        for (const change of timelineRef.current) dispatch(appendTutorLiveSession(memoryKey, change) as never)
    }, [dispatch, memoryKey, timelineKey])

    // Le dernier passage sur un élève : quand on le quitte, le repère « Depuis ton dernier passage ».
    const previousSelected = useRef(selection.selectedId)
    useEffect(() => {
        const left = previousSelected.current
        previousSelected.current = selection.selectedId
        if (left != null && left !== selection.selectedId && memoryKey != null)
            dispatch(seenTutorLiveSession(memoryKey, left) as never)
    }, [dispatch, memoryKey, selection.selectedId])

    // Le champ vise l'élève regardé ; en mosaïque, aucun n'est regardé : il vise tout le groupe (il disait « pour
    // Thérèse P. » sous « Tous », marche du 03/11). Une demande pour plusieurs (la photo du chat du groupe) le tourne
    // vers le groupe jusqu'au prochain élève regardé.
    const [forGroup, setForGroup] = useState(false)
    useEffect(() => setForGroup(false), [selection.selectedId])
    const watchStudent = useCallback(() => setForGroup(false), [])
    const watchedId = options.mosaic !== true ? (selection.selectedId ?? null) : null
    const targetId = forGroup ? null : watchedId

    // ── Les gestes, le résumé, la génération.
    const [preview, setPreview] = useState<{ studentId: string; uri: string } | null>(null)
    const sam = useTutorLiveFeedSamSummary({
        liveSessionId,
        users,
        firstnameOf,
        enabled: checkpointRh,
        onOpenPhoto: (studentId, uri) => setPreview({ studentId, uri }),
    })
    const send = room.live?.send ?? null
    const actions = useTutorLiveFeedActions({
        liveSessionId,
        meId: room.me.id,
        send,
        selection,
        studentIds,
        present,
        soundModeIds,
        firstnameOf,
        isWeb: IS_WEB,
        memoryKey,
        openSummary: sam.open,
    })
    // « <champion> propose » est appelé après la génération : elle lit son envoi en cours par une ref, au geste.
    const proposalsRef = useRef<{ sending: boolean } | null>(null)
    const similarSourceRef = useRef(options.similarSource)
    similarSourceRef.current = options.similarSource
    const similarSource = useCallback((studentId: string) => {
        const source = similarSourceRef.current?.(studentId)
        return source != null ? generationFrom(source) : null
    }, [])
    // Les listes d'un élève (ses activités, ses objectifs) : celles des inscrits de la séance, que la pellicule lit
    // (`getLiveSessionV2`), puis celles du compte du tuteur (relues par `getStudent`). La séance d'abord : le compte
    // n'a un élève qu'une fois sa fiche lue (vu en recette : sans elle, le pré-remplissage ne trouvait rien).
    const studentLists = useMemo(
        () => [...(users as (ApiStudentLists & { id?: string | number })[]), ...slices.accountStudents],
        [users, slices.accountStudents]
    )
    // Ce que le champ reprend pour un élève (`generationPrefill`) : lu au geste, sur les listes du moment.
    const prefillInputs = useRef({ studentLists, students, liveSessionId })
    prefillInputs.current = { studentLists, students, liveSessionId }
    const sessionStartedAt = room.startedAt
    const prefill = useCallback(
        (studentId: string) => {
            const inputs = prefillInputs.current
            const raw = inputs.studentLists.find((candidate) => String(candidate?.id) === studentId)
            const objectiveStudent = raw as ApiObjectiveStudent | undefined
            return generationPrefill({
                student: raw,
                // L'activité ouverte au centre pour lui passe avant tout (Wajih, live du 04/11 au soir).
                open: similarSourceRef.current?.(studentId) ?? null,
                sessionStartedAt,
                onScreenId:
                    inputs.students
                        .find((student) => student.id === studentId)
                        ?.activities.find((activity) => activity.onScreen === true)?.id ?? null,
                objectiveSubjectIds: sessionSubjectIds(
                    objectiveStudent,
                    resolveSessionObjective(objectiveStudent, inputs.liveSessionId)
                ),
            })
        },
        [sessionStartedAt]
    )
    const generation = useTutorLiveFeedGeneration({
        targetId,
        studentIds,
        firstnameOf,
        liveSessionId,
        quizAvailable,
        send,
        memoryKey,
        helperName: room.helperName,
        notify: actions.notify,
        watching: options.mosaic === true ? null : (selection.selectedId ?? null),
        presenting: options.presenting === true,
        similarSource,
        // Ni pendant une présentation : la zone de dépôt couvrirait ce que voient les élèves.
        roomDrop: options.chatOpen !== true && options.centerOnly !== true && options.presenting !== true,
        isCreateLocked: () => proposalsRef.current?.sending === true,
        prefill,
    })

    // « <champion> propose » : une activité faite d'avance sur un rendu qui attend (bornée, comptée).
    // Les rendus vus dans la séance, validés ou non : la correction n'existe qu'une fois le rendu validé, la proposition
    // naît donc du signal du rendu (que seul un geste du tuteur retire), pas de la carte qui attend la validation.
    const handIns = signals
        .filter((signal) => signal.type === "exercise_done" && signal.activityId != null)
        .map((signal) => ({ studentId: signal.studentId, activityId: signal.activityId as string }))
    const proposals = useTutorLiveFeedProposals({
        handIns,
        memory: slices.helperProposals,
        accountStudents: studentLists,
        firstnameOf,
        send,
        telemetry: room.telemetry,
        tutorGenerating: generation.busy,
        tutorCreating: generation.isCreating,
        presenting: options.presenting === true,
        quizAvailable,
        notify: actions.notify,
        helperName: room.helperName,
    })

    proposalsRef.current = proposals

    // Les lignes relues du serveur ne changent qu'avec les conversations ; les prénoms, eux, suivent la salle.
    const serverLines = useMemo(
        () => ({ helper: helperLines(users, sessionId), quiz: quizLines(users, sessionId) }),
        [users, sessionId]
    )
    const transient = useMemo<TutorLiveTransientEntry[]>(
        () => [
            ...serverLines.helper.map((line) => ({
                studentId: line.studentId,
                at: line.at,
                entry: {
                    kind: "helper" as const,
                    id: line.id,
                    at: isoOf(line.at),
                    title: line.title,
                    text: line.text,
                },
            })),
            ...serverLines.quiz.map((line) => ({
                studentId: line.studentId,
                at: line.at,
                entry: quizEntryOf(line, names.find((name) => name.id === line.studentId)?.firstname ?? "L'élève"),
            })),
            ...proposals.transient,
            ...generation.transient,
        ],
        [serverLines, names, generation.transient, proposals.transient]
    )

    const startedHereAt =
        memory?.createdAt != null && memory.createdAt - room.startedAt > STARTED_ELSEWHERE_MS
            ? memory.createdAt
            : undefined
    const view = tutorLiveFeedView({
        cards,
        records: withTimeline,
        transient,
        students: names,
        selectedId: watchedId ?? undefined,
        withHelper: ongoing,
        seen: memory?.seen ?? {},
        startedHereAt,
        now,
        activitiesOf: (studentId) => students.find((student) => student.id === studentId)?.activities ?? [],
    })
    // Le fil de chaque élève, depuis le début du live (l'heure officielle, ou le premier Dialogue s'il est plus tôt) :
    // sa tuile le porte, l'onglet « Fil » le relit ligne par ligne. Il finit à maintenant : il change à chaque rendu.
    // Ses entrées dans la salle notées par le serveur : relues à l'entrée dans l'appel, pas à chaque rendu.
    const joins = useMemo(() => joinedCallTimes(slices.todayLogs ?? []), [slices.todayLogs])
    const { startedAt: sessionStart, threads } = tutorSessionThreads({
        records: withTimeline,
        students,
        officialStart: room.startedAt,
        now,
        clock: clockOf,
        transient,
        joins,
    })

    // La même référence tant que les comptes ne changent pas : la pellicule les lit à chaque rendu (`pendingOf`).
    const pendingKey = JSON.stringify(view.pending)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const pending = useMemo(() => view.pending, [pendingKey])

    const nextWake = tutorLiveFeedNextWake({
        cards,
        records,
        outOfAppSince,
        samSentAtByKey: slices.samSentAtByKey,
        now,
    })
    useEffect(() => {
        if (nextWake == null) return
        const timer = setTimeout(wake, Math.max(0, nextWake - Date.now()))
        return () => clearTimeout(timer)
    }, [nextWake])

    const cardsRef = useRef(cards)
    cardsRef.current = cards
    const onAction = useCallback(
        (itemId: string, actionId: string) => {
            const card = cardsRef.current.find((c) => c.id === itemId)
            if (card != null) actions.run(card, actionId)
        },
        [actions]
    )
    // La main levée pendant que le masteur parle à un autre : sa carte du bac propose le champion.
    const handKey = cards
        .filter((card) => card.signal === "hand_raised" && card.item.action?.id === "send_sam")
        .map((card) => card.studentId)
        .join(",")
    const handChoice = useMemo(() => (handKey === "" ? [] : handKey.split(",")), [handKey])
    const onHandChoice = useCallback(
        (studentId: string, choice: "send_sam" | "decline") => {
            const card = cardsRef.current.find((c) => c.signal === "hand_raised" && c.studentId === studentId)
            if (card == null) return
            if (choice === "decline") actions.dismiss(card)
            else actions.run(card, choice)
        },
        [actions]
    )
    const onDismiss = useCallback(
        (itemId: string) => {
            const card = cardsRef.current.find((c) => c.id === itemId)
            if (card == null) return
            if (card.signal === "sam_summary") track(SUMMARY_POPUP_EVENTS.close)
            actions.dismiss(card)
        },
        [actions]
    )

    const [open, setOpen] = useState(false)
    // La relecture s'ouvre au centre : le tiroir de la session (sous 1 024 px) se range, il la couvrirait.
    const review = proposals.review ?? generation.review
    // La relecture et « Plus d'options » s'ouvrent au centre : le tiroir de la session (sous 1 024 px) se range, il
    // les couvrirait.
    const centered = review?.open === true || generation.configurator?.open === true
    useEffect(() => {
        if (centered) setOpen(false)
    }, [centered])

    const generationRef = useRef(generation)
    generationRef.current = generation
    const selectionRef = useRef(selection)
    selectionRef.current = selection

    // « Actualiser » : les trois lectures du legacy, désactivé tant qu'elles volent (`request`).
    const [refreshing, setRefreshing] = useState(false)
    const refreshingRef = useRef(false)
    const onRefresh = useCallback(() => {
        if (refreshingRef.current || liveSessionId == null) return
        refreshingRef.current = true
        setRefreshing(true)
        const studentId = selectionRef.current.selectedId
        const reads: Promise<unknown>[] = [
            apiCall(dispatch as never, liveServiceV2.getLiveSessionV2, { routeParams: { id: liveSessionId } }),
            apiCall(dispatch as never, liveServiceV2.getCallV2, { routeParams: { id: liveSessionId } }),
        ]
        if (studentId != null)
            reads.push(
                apiCall(dispatch as never, schoolAssessmentsService.getSchoolAssessments, {
                    urlParams: { student_id: rawId(studentId), upcoming: true },
                })
            )
        void Promise.allSettled(reads).then(() => {
            refreshingRef.current = false
            setRefreshing(false)
        })
    }, [dispatch, liveSessionId])

    const generate = useCallback((request: TutorLiveGenerateCall) => {
        // Un élève nommé devient l'élève regardé ; aucun, tout le groupe ; plusieurs, ces élèves-là, ensemble (une
        // activité commune).
        const [only] = request.studentIds
        const single = request.studentIds.length === 1
        if (single) selectionRef.current.select(only)
        setForGroup(!single)
        setOpen(true)
        // « Ajouter une activité » sans pièce : le champ ressort, pré-rempli, comme « + Activité ».
        if (request.from == null && request.attachment == null) {
            generationRef.current.summon(single ? only : null)
            return
        }
        const attachment = request.attachment
        // Une photo du chat reprend le chapitre de l'activité que l'élève a devant lui.
        const chapter =
            attachment != null && request.origin?.kind === "chat" && single
                ? chapterOnScreen(
                      studentsRef.current.find((student) => student.id === only),
                      accountStudentsRef.current
                  )
                : undefined
        generationRef.current.request({
            target: single ? only : null,
            ids: request.studentIds.length > 1 ? request.studentIds : undefined,
            from: request.from != null ? generationFrom(request.from) : undefined,
            attachment:
                attachment != null
                    ? { uri: attachment.uri, name: attachment.name, mimeType: attachment.mimeType }
                    : undefined,
            origin: request.origin,
            chapter: chapter ?? undefined,
        })
    }, [])
    const studentsRef = useRef(students)
    studentsRef.current = students
    const accountStudentsRef = useRef(slices.accountStudents)
    accountStudentsRef.current = slices.accountStudents

    const { sheet, editor } = useTutorLiveFeedSheet({
        generation,
        preview:
            preview != null
                ? {
                      uri: preview.uri,
                      onClose: () => setPreview(null),
                      onCreateActivity: () => {
                          const { studentId, uri } = preview
                          setPreview(null)
                          selection.select(studentId)
                          setForGroup(false)
                          const pdf = /\.pdf($|\?)/i.test(uri)
                          generation.attach(
                              {
                                  uri,
                                  name: `checkpoint_${Date.now()}`,
                                  mimeType: pdf ? "application/pdf" : "image/png",
                              },
                              studentId
                          )
                      },
                  }
                : null,
    })

    const addActivity = useCallback(() => {
        setOpen(true)
        generationRef.current.summon()
    }, [])

    // La proposition du champion encore en attente pour l'élève visé : elle ressort en tête du champ.
    const pendingProposal = targetId != null ? proposals.pendingFor(targetId) : null
    const composer =
        generation.composer != null && pendingProposal != null
            ? {
                  ...generation.composer,
                  proposal: {
                      helper: room.helper,
                      title: pendingProposal.title,
                      reason: pendingProposal.reason,
                      sendLabel: pendingProposal.sendLabel,
                      sending: pendingProposal.sending,
                      onSee: () => proposals.onAction(pendingProposal.entryId, "see"),
                      onSend: () => proposals.onAction(pendingProposal.entryId, "send"),
                      onDismiss: () => proposals.onAction(pendingProposal.entryId, "dismiss"),
                  },
              }
            : generation.composer

    // Les gestes des lignes de la génération : la carte « prêtes », « <champion> propose ».
    const onReady = generation.onReady
    const onProposal = proposals.onAction
    const onGeneration = useCallback(
        (entryId: string, action: "review" | "sendFirst" | "see" | "send" | "dismiss") => {
            if (action === "review" || action === "sendFirst") onReady(action)
            else onProposal(entryId, action)
        },
        [onReady, onProposal]
    )

    const zone: TutorLiveFeedZone = {
        items: view.items,
        onAction,
        onDismiss,
        open,
        onToggle: () => setOpen((value) => !value),
        decisions: view.decisions,
        // L'aurore de la tête : le champion parle avec l'élève regardé, sinon avec un autre.
        helperWith: firstnameWithHelper(ongoing, watchedId, firstnameOf),
        onResult: generation.onResult,
        // La puce dépliée se pose dans le champ, sur place (jamais une modale par-dessus la salle).
        composer: composer != null && editor != null ? { ...composer, editor } : composer,
        onGeneration,
        onPeek: generation.onPeek,
        review,
        configurator: generation.configurator,
        peek: generation.peek,
        drop: generation.dropping
            ? {
                  firstname: targetId != null ? firstnameOf(targetId) : null,
                  avatarUrl:
                      targetId != null ? students.find((student) => student.id === targetId)?.avatarUrl : undefined,
              }
            : null,
    }

    // Ni `snackbarAutomatic` ni `dialogAutomatic` : chaque surface du fil naît d'un geste du masteur (l'accusé
    // d'une carte ou d'une création, le recadrage d'un collage, l'aperçu d'une photo), et le
    // résumé du champion ne s'ouvre que sur « Lire le résumé ». Les signaux, eux, vivent dans la colonne, que
    // la page en « centre seul » ne montre pas.
    return {
        zone,
        sessionStartedAt: sessionStart,
        watchStudent,
        threads,
        replay:
            watchedId != null
                ? {
                      helper: room.helper,
                      firstname: firstnameOf(watchedId),
                      thread: threads[watchedId] ?? null,
                      history: view.session.history,
                      startedHere: view.session.startedHere,
                      // « Ouvrir » une activité du fil la montre au centre.
                      onOpenActivity: (activityId) => selection.openActivityFor(watchedId, activityId),
                      onGeneration,
                      onPeek: generation.onPeek,
                      peekId: generation.peek != null ? String(generation.peek.proposal.id) : null,
                  }
                : null,
        layer: { snackbar: actions.snackbar, sheet },
        samSummary: sam.props,
        generate,
        pending,
        handChoice,
        onHandChoice,
        feedPending: actions.pending,
        refresh: { onRefresh, refreshing },
        addActivity,
    }
}
